import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { developerService, DeveloperLogParams } from '../developerService';

const POLL_INTERVAL_MS = 5000;

export function useDeveloperOverview(enabled = true) {
  return useQuery({
    queryKey: ['developer', 'overview'],
    queryFn: () => developerService.getOverview(),
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
    staleTime: 0,
    enabled,
  });
}

export function useDeveloperLogs(params: DeveloperLogParams, enabled = true) {
  return useQuery({
    queryKey: ['developer', 'logs', params],
    queryFn: () => developerService.getLogs(params),
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
    staleTime: 0,
    enabled,
  });
}

export function useDeveloperIssues(enabled = true) {
  return useQuery({
    queryKey: ['developer', 'issues'],
    queryFn: () => developerService.getIssues(100),
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
    staleTime: 0,
    enabled,
  });
}

export function useDeveloperRequests(enabled = true) {
  return useQuery({
    queryKey: ['developer', 'requests'],
    queryFn: () => developerService.getRequests(100),
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
    staleTime: 0,
    enabled,
  });
}

export function useDeveloperAdmins() {
  return useQuery({
    queryKey: ['developer', 'admins'],
    queryFn: () => developerService.listAdmins(),
    staleTime: 0,
  });
}

export function useDeveloperFrontendMode() {
  return useQuery({
    queryKey: ['developer', 'frontend-mode'],
    queryFn: () => developerService.getFrontendMode(),
    staleTime: 0,
  });
}

export function useSetFrontendMode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: developerService.setFrontendMode,
    meta: { successMessage: 'Frontend mode updated' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'frontend-mode'] });
    },
  });
}

export function useCleanupModules() {
  return useQuery({
    queryKey: ['developer', 'cleanup-modules'],
    queryFn: () => developerService.getCleanupModules(),
    staleTime: 0,
  });
}

export function useDeveloperCacheOverview(enabled = true) {
  return useQuery({
    queryKey: ['developer', 'cache'],
    queryFn: () => developerService.getCacheOverview(),
    staleTime: 0,
    enabled,
  });
}

export function useInvalidateCache() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: developerService.invalidateCache,
    meta: { successMessage: 'Cache invalidated' },
    onSuccess: () => {
      // Clearing Redis does not retire responses already held by TanStack, so
      // mark every client query stale as well. Without this the screen that
      // prompted the flush keeps showing the same figures until its own
      // staleTime expires, even though the server would now serve fresh reads.
      // This also refreshes the cache overview (`['developer','cache']`).
      void queryClient.invalidateQueries();
    },
  });
}

export function useCleanModules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ modules, confirmation }: { modules: string[]; confirmation: string }) =>
      developerService.cleanModules(modules, confirmation),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'cleanup-modules'] });
    },
  });
}

export function useUpsertAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: developerService.upsertAdmin,
    meta: { successMessage: 'Super admin saved' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'admins'] });
    },
  });
}

export function useResetDatabase() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: developerService.resetDatabase,
    // Reset wipes the session; the page shows its own success/redirect UI.
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

/**
 * Read-only scan for payments left on cancelled/deleted IPD stays. Runs only
 * after the operator presses "Scan" (`enabled`), so opening the tab costs
 * nothing.
 */
export function useFinanceIntegrityScan(enabled: boolean) {
  return useQuery({
    queryKey: ['developer', 'finance-integrity'],
    queryFn: () => developerService.scanFinanceIntegrity(),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

export function useLedgerReconciliation(enabled: boolean) {
  return useQuery({
    queryKey: ['developer', 'ledger-reconciliation'],
    queryFn: () => developerService.getLedgerReconciliation(),
    enabled,
    staleTime: 0,
    retry: false,
  });
}

export function useBackfillLedger() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: developerService.backfillLedger,
    meta: { hideToast: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['developer', 'ledger-reconciliation'] });
    },
  });
}

export function useRepairLedger() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: developerService.repairLedger,
    // The card renders its own result banner.
    meta: { hideToast: true },
    onSuccess: () => {
      // Repairs rewrite the journal, cash book and invoice balances, so every
      // screen that embeds those figures must refetch.
      void queryClient.invalidateQueries();
    },
  });
}

export function useClearFalseData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      paymentIds,
      confirmation,
    }: {
      paymentIds: string[];
      confirmation: string;
    }) => developerService.clearFalseData(paymentIds, confirmation),
    // The panel renders its own summary banner.
    meta: { hideToast: true },
    onSuccess: () => {
      // Clearing rewrites payments, cashbook, invoices and the server cache, so
      // every screen that embeds these figures must refetch.
      void queryClient.invalidateQueries();
    },
  });
}

export function useAiProviderConfig() {
  return useQuery({
    queryKey: ['developer', 'ai-config'],
    queryFn: () => developerService.getAiConfig(),
    staleTime: 0,
  });
}

export function useSaveAiProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: developerService.saveAiConfig,
    meta: { successMessage: 'AI provider saved' },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'ai-config'] });
    },
  });
}

export function useTestAiProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => developerService.testAiProvider(),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['developer', 'ai-config'] });
    },
  });
}

export function useAiFinanceReview() {
  return useMutation({
    mutationFn: () => developerService.reviewFinanceWithAi(),
    meta: { hideToast: true },
  });
}

export function useAiModels() {
  return useMutation({
    mutationFn: developerService.listAiModels,
    meta: { hideToast: true },
  });
}

export function useAskAi() {
  return useMutation({
    mutationFn: (question: string) => developerService.askAi(question),
    meta: { hideToast: true },
  });
}
